import React from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import BackButton from '../../../components/custom/BackButton';
import ActionIcon from '../../../components/custom/ActionIcon';
import VirtualTable from '../../../components/custom/VirtualTable';
import { ChoiceEmployee, Team, TeamMember } from '../../../globals/interfaces';
import apiClient from '../../../apiClient';
import useSWR from 'swr';
import useUnit from '../../../customHooks/useUnit';
import { getTsDate, notify } from '../../../utils';
import useUser from '../../../customHooks/useUser';
import { mutate } from 'swr';
import useLoading from '../../../customHooks/useLoading';

const BASE_TABLE_MAPPING = [{
    name: '',
    value: 's_photo_link',
    image: true,
    imageHeight: '50px',
    imageWidth: '50px',
    borderRadius: '50px',
    customWidth: 70
}, {
    name: 'Employee Name',
    value: 'displayName'
}, {
    name: 'Email',
    value: 's_email'
}];

const teamMembersFetcher = async (key: string): Promise<Array<TeamMember>> => {
    try {
        const res = await apiClient.get(key);
        return res.data;
    } catch (err) {
        return [];
    }
}

interface ModalManageMembersProps {
    modal: boolean;
    setModal: (value: boolean) => void;
    selectedTeamId: number | undefined;
    selectedTeam: Team | undefined;
    employees: Array<ChoiceEmployee>;
    setMemberModified: React.Dispatch<React.SetStateAction<number>>;
    updateEmployeesWithShiftsApi: string;
  }

export default function ModalManageMembers ({
    modal,
    setModal,
    selectedTeamId,
    selectedTeam,
    employees,
    setMemberModified,
    updateEmployeesWithShiftsApi
}: ModalManageMembersProps) {

    const { user } = useUser();
    const { setLoading } = useLoading();

    const {
        data: members,
        mutate: mutateMembers
    } = useSWR(selectedTeamId ? `/team-member/${selectedTeamId}` : null, teamMembersFetcher, {
        fallbackData: []
    });


    let membersMap = {};
    for (const member of members) {
        membersMap[member.email] = true;
    }
    const renderEmployees = employees.filter(emp => membersMap[emp.s_email] === undefined);

    const updateTeamMembers = () => {
        setMemberModified(prev => prev + 1);
    }

    const addMember = async (employee: ChoiceEmployee) => {
        setLoading(true);
        const data: Omit<TeamMember, 'id'> = {
            email: String(employee.s_email),
            teamId: selectedTeamId,
            createdAt: getTsDate(),
            createdBy: user.s_email
        };

        try {
            const res = await apiClient.post(`/team-member`, data);
            mutateMembers();
            updateTeamMembers();
            notify('Added');
            // handleTriggerRefresh();
        } catch (err) {
            alert(err);
        }
        setLoading(false);
    }

    const deleteMember = async (member: TeamMember) => {
        setLoading(true);
        try {
            const res = await apiClient.delete(`/team-member/${member.id}`);
            mutateMembers();
            updateTeamMembers();
            notify('Deleted');
            // handleTriggerRefresh();
        } catch (err) {
            alert(err);
        }
        setLoading(false);
    }

    const toggle = () => setModal(!modal);

    if (!selectedTeam) return null;

    return (
        <CustomModal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>Manage Team Members for {selectedTeam.name}</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <FlexContainer>
                    <div>
                        <h6>{user.s_unit} Employees</h6>
                        <VirtualTable<ChoiceEmployee>
                            data={renderEmployees}
                            // @ts-ignore
                            mapping={[...BASE_TABLE_MAPPING, {
                                name: '',
                                value: 'fas fa-plus',
                                icon: true,
                                function: employee => addMember(employee)
                            }]}
                            numRows={15}
                            enableClick={false}
                        />
                    </div>
                    <div>
                        <h6>Members</h6>
                        <VirtualTable<TeamMember>
                            data={members}
                            // @ts-ignore
                            mapping={[...BASE_TABLE_MAPPING, {
                                name: '',
                                value: 'fas fa-trash',
                                icon: true,
                                function: member => deleteMember(member)
                            }]}
                            numRows={15}
                        />
                    </div>
                </FlexContainer>
            </ModalBody>
        </CustomModal>
    );
}

const CustomModal = styled(Modal)`
    min-width: 60%;
`;

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const FlexContainer = styled.div`
    display: flex;
    gap: 25px;
    
    div {
        flex: 1;
    }
`;